import type { ContentPack, BuildState, Palette, Part, MountSpec, Anchor, Side } from '../packs/types';

export function renderBuild(
  ctx: CanvasRenderingContext2D,
  build: BuildState,
  pack: ContentPack,
  palette: Palette,
  t = 0,
): void {
  const size = pack.rasterSize;
  if (pack.drawBackground) {
    pack.drawBackground(ctx, build, palette);
  } else {
    ctx.clearRect(0, 0, size, size);
  }
  const baseCategoryId = pack.metadata?.baseCategoryId ?? 'hull';
  const hullId = build[baseCategoryId] as string | undefined;
  if (!hullId) return;
  const hull = pack.hulls[hullId];
  if (!hull) return;

  const m = hull.mounts;
  const bgCaps: Array<() => void> = [];
  let hullDrawn = false;

  for (const key of pack.renderOrder) {
    if (key === baseCategoryId) {
      hull.drawBody(ctx, palette, t);
      bgCaps.forEach(fn => fn());
      bgCaps.length = 0;
      hullDrawn = true;
      continue;
    }

    const partId = build[key] as string | null | undefined;
    if (!partId) continue;
    const part = pack.parts[partId];
    if (!part) continue;

    const slotIds = pack.compositeSlots[part.requires] ?? [part.requires];

    for (const slotId of slotIds) {
      const anchor = resolveAnchor(slotId, m, pack.parts, build);
      if (!anchor) continue;
      const mount: MountSpec | undefined = m[slotId];
      const side: Side | undefined = slotId.endsWith('_left') ? 'left'
        : slotId.endsWith('_right') ? 'right' : undefined;

      if (!hullDrawn) {
        part.draw(ctx, anchor, palette, side);
        if (mount?.cap) {
          const cap = mount.cap;
          bgCaps.push(() => cap(ctx, palette));
        }
      } else {
        mount?.base?.(ctx, palette);
        pickFrame(part, t).draw(ctx, anchor, palette, side);
        mount?.cap?.(ctx, palette);
      }
    }
  }
}

function pickFrame(part: Part, t: number): Pick<Part, 'draw'> {
  if (part.frames && part.frames.length > 0) {
    return part.frames[Math.floor((t * 6) % part.frames.length)];
  }
  return part;
}

function resolveAnchor(
  slotId: string,
  mounts: Record<string, MountSpec>,
  parts: Record<string, Part>,
  build: BuildState,
): Anchor | null {
  if (mounts[slotId]) return mounts[slotId].anchor;
  for (const partId of Object.values(build)) {
    if (typeof partId !== 'string') continue;
    const part = parts[partId];
    const prov = part?.provides?.[slotId];
    if (!prov) continue;
    const base = mounts[prov.baseSlot];
    if (!base) continue;
    return { x: base.anchor.x + (prov.offsetX ?? 0), y: base.anchor.y + prov.offsetY };
  }
  return null;
}
